import { formatPhone, type SearchResult, SearchSelect } from "@majitap/ui";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, ok } from "../../api";
import type { Billee } from "../../invoices";
import { useDebounced } from "../../search";

export type Payer = { billee: Billee; label: string };

/** Who an invoice bills (or, with another `label`, whose place): a business or a person, found by search. */
export function PayerPicker({
  value,
  onChange,
  label = "Bill to",
}: {
  value: Payer | null;
  onChange: (payer: Payer | null) => void;
  label?: string;
}) {
  const [kind, setKind] = useState<"business" | "customer">(
    value && "customer_id" in value.billee ? "customer" : "business",
  );
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim());
  const found = useQuery({
    queryKey: ["payer-search", kind, q],
    enabled: q.length >= 2,
    queryFn: async (): Promise<SearchResult[]> =>
      kind === "business"
        ? (await ok(api.GET("/v1/admin/businesses", { params: { query: { q, limit: 8 } } }))).data.map((b) => ({
            id: b.id,
            label: b.business_name,
            description: formatPhone(b.phone) || undefined,
          }))
        : (await ok(api.GET("/v1/admin/customers", { params: { query: { q, limit: 8 } } }))).data.map((c) => ({
            id: c.id,
            label: c.user.name,
            description: formatPhone(c.user.phone_number) || undefined,
          })),
  });
  const selected: SearchResult | null = value
    ? { id: "customer_id" in value.billee ? value.billee.customer_id : value.billee.business_id, label: value.label }
    : null;
  return (
    <>
      <SegmentedControl
        label={label}
        value={kind}
        onValueChange={(v) => {
          setKind(v as "business" | "customer");
          setQuery("");
          onChange(null);
        }}
        options={[
          { value: "business", label: "A business" },
          { value: "customer", label: "A customer" },
        ]}
      />
      <SearchSelect
        label={kind === "business" ? "Business" : "Customer"}
        placeholder={kind === "business" ? "Search by business name" : "Search by name or phone number"}
        value={selected}
        onChange={(r) =>
          onChange(
            r ? { billee: kind === "business" ? { business_id: r.id } : { customer_id: r.id }, label: r.label } : null,
          )
        }
        query={query}
        onQueryChange={setQuery}
        pending={query.trim() !== q || found.isFetching}
        results={found.data ?? []}
      />
    </>
  );
}
